/**
 * Grats Meet embed client — a JavaScript port of `@lmscampus/video-embed`.
 *
 * ── Why this is vendored rather than installed ────────────────────────────
 * The package lives in the video platform's own pnpm workspace and is not
 * published to a registry. A `file:` dependency would point at a path that
 * exists on one developer's machine and on no build agent, so `npm ci` would
 * fail everywhere the app is actually built. The whole module is under 200
 * lines and the protocol is versioned, so a copy is cheaper than a private
 * registry — and swapping back to the package later is one import line.
 *
 * ⚠️ `EMBED_PROTOCOL_VERSION` and `EMBED_CHANNEL` below MUST match
 * `videosdk_fe/packages/video-embed/src/protocol.ts`. The platform's own
 * `/embed` page is the other half of this contract; if the two drift, the
 * iframe loads and then sits silently at "Waiting for the host application"
 * because neither side recognises the other's messages.
 *
 * ── What it does ──────────────────────────────────────────────────────────
 * Puts the platform's complete meeting UI — stage, participants, chat,
 * controls, screen share, recording, device switching — inside an element of
 * ours, in an iframe served by the platform origin. That is what keeps the
 * media engine, its dependencies and the meeting UI out of this bundle, and
 * what makes it impossible for a platform credential to be read from LMS
 * script or for the two stylesheets to collide.
 *
 * The grant is handed over by `postMessage` AFTER the frame says it is
 * listening — never in the iframe URL, because a URL ends up in history, server
 * logs and referrer headers, and the grant is a credential.
 */

export const EMBED_PROTOCOL_VERSION = 1;
export const EMBED_CHANNEL = 'lmscampus.video.embed';

function isEmbedMessage(data) {
  return (
    typeof data === 'object' &&
    data !== null &&
    data.channel === EMBED_CHANNEL &&
    typeof data.type === 'string'
  );
}

/**
 * A mounted meeting.
 *
 * Methods are queued until the embed reports `ready`, so a caller can act on
 * the instance immediately after `mount` without racing the iframe's load.
 */
class GratsVideoEmbedInstance {
  constructor(frame, origin, config) {
    this.frame = frame;
    this.origin = origin;
    this.config = config;
    this.listeners = new Map();
    this.pending = [];
    this.ready = false;
    this.destroyed = false;

    this.receive = this.receive.bind(this);
    window.addEventListener('message', this.receive);
  }

  receive(event) {
    // Two gates, both necessary: the origin check stops any other frame or tab
    // impersonating the meeting, and the source check stops a SECOND embed on
    // the same page delivering its events to this instance.
    if (event.origin !== this.origin) return;
    if (this.frame && event.source !== this.frame.contentWindow) return;
    if (!isEmbedMessage(event.data)) return;

    if (event.data.type === 'ready' && !this.ready) {
      this.ready = true;
      this.post({ channel: EMBED_CHANNEL, type: 'configure', config: this.config });
      const queued = this.pending.splice(0);
      for (const message of queued) this.post(message);
    }

    const handlers = this.listeners.get(event.data.type);
    if (handlers) for (const handler of handlers) handler(event.data.payload);
  }

  post(message) {
    if (this.destroyed) return;
    if (!this.ready && message.type !== 'configure') {
      this.pending.push(message);
      return;
    }
    if (this.frame && this.frame.contentWindow) {
      this.frame.contentWindow.postMessage(message, this.origin);
    }
  }

  /** Subscribe. Returns an unsubscribe function. */
  on(event, listener) {
    const set = this.listeners.get(event) || new Set();
    set.add(listener);
    this.listeners.set(event, set);
    return () => set.delete(listener);
  }

  setMicrophoneEnabled(enabled) {
    this.post({ channel: EMBED_CHANNEL, type: 'setMicrophone', enabled });
  }

  setCameraEnabled(enabled) {
    this.post({ channel: EMBED_CHANNEL, type: 'setCamera', enabled });
  }

  /** Leave the meeting but keep the frame mounted, so we can show our own ending. */
  leave() {
    this.post({ channel: EMBED_CHANNEL, type: 'leave' });
  }

  /**
   * Remove the meeting entirely.
   *
   * ⚠️ Tearing down the iframe is what actually RELEASES THE CAMERA AND
   * MICROPHONE. Unmounting a React component without calling this leaves the
   * camera light on after the user has navigated away, which people reasonably
   * report as a bug.
   */
  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    window.removeEventListener('message', this.receive);
    if (this.frame) this.frame.remove();
    this.frame = null;
    this.listeners.clear();
  }
}

function resolveContainer(target) {
  const element = typeof target === 'string' ? document.querySelector(target) : target;
  if (!element) throw new Error(`GratsVideoEmbed: no element matched ${String(target)}`);
  return element;
}

/**
 * Put a working meeting inside `target`.
 *
 * @param {string|HTMLElement} target
 * @param {Object} options
 * @param {string} options.baseUrl   platform origin, e.g. https://meetings.gratsit.com
 * @param {Object} options.grant     the grant our server minted for this person
 * @param {boolean} [options.hideInvite]  the LMS issues invitations, not the platform
 * @param {{accent?: string}} [options.theme]
 * @param {string} [options.width]
 * @param {string} [options.height]
 * @returns {GratsVideoEmbedInstance}
 */
export function mount(target, options) {
  const { baseUrl, width = '100%', height = '100%', ...config } = options || {};
  if (!baseUrl) throw new Error('GratsVideoEmbed: baseUrl is required');
  if (!config.grant && !config.joinCode) {
    throw new Error('GratsVideoEmbed: pass either a `grant` or a `joinCode`');
  }

  const origin = new URL(baseUrl).origin;
  const container = resolveContainer(target);

  const frame = document.createElement('iframe');
  frame.src = `${origin}/embed`;
  frame.title = 'Live class';
  frame.style.width = width;
  frame.style.height = height;
  frame.style.border = '0';
  frame.style.display = 'block';
  // Without these the browser refuses the camera, the microphone and screen
  // sharing INSIDE the frame, and the class fails in a way that looks like a
  // platform bug rather than a missing attribute here.
  //
  // ⚠️ The origin is UNQUOTED. The `allow` attribute and the
  // `Permissions-Policy` HTTP header are two grammars for the same policy: the
  // header is a structured field and quotes its origins -- `camera=("https://x")`
  // -- while this attribute takes bare ones. A quoted origin here does not parse
  // as an origin, so the allowlist comes out EMPTY and the feature is disallowed
  // for the frame. It surfaces as `getDisplayMedia ... disallowed by permissions
  // policy` and as a microphone that cannot be unmuted, never as a bad attribute.
  frame.allow = [
    'camera',
    'microphone',
    'display-capture',
    'autoplay',
    'clipboard-write',
    'fullscreen',
  ]
    .map((feature) => `${feature} ${origin}`)
    .join('; ');
  frame.allowFullscreen = true;

  container.appendChild(frame);
  return new GratsVideoEmbedInstance(frame, origin, config);
}

const GratsVideoEmbed = { mount };
export default GratsVideoEmbed;
